import type { FeatureEvents, ScreenEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject } from '../new-analytics-types';

// https://product-fabric.atlassian.net/wiki/spaces/SA/pages/5553848338/UGC+Data+Use+-+Analytics+Spec#Screen-Events---%5BinlineCard%5D
type ScreenKeys =
  | 'ugcDataUsePageScreenEvent'
  | 'ugcDataUseFocusedTaskSelectAppsScreenEvent'
  | 'ugcDataUseFocusedTaskSelectPreferenceScreenEvent'
  | 'ugcDataUseFocusedTaskReviewScreenEvent'
  | 'ugcDataUseV2PageScreenEvent'
  | 'ugcDataUseV2SelectSpacesScreenEvent'
  | 'ugcDataUseV2SelectAppsScreenEvent'
  | 'ugcDataUseV2SelectConnectorsScreenEvent'
  | 'ugcDataUseV2BenefitScreenEvent';

// https://product-fabric.atlassian.net/wiki/spaces/SA/pages/5553848338/UGC+Data+Use+-+Analytics+Spec#UI-Events---%5BinlineCard%5D
type UIEventKeys =
  | 'ugcDataUseUpdateSettingsClickEvent'
  | 'ugcDataUseConfirmSettingsClickEvent'
  | 'ugcDataUsePageDwelledEvent'
  | 'ugcDataUseFocusedTaskSelectAppsScreenDwelledEvent'
  | 'ugcDataUseFocusedTaskSelectPreferenceScreenDwelledEvent'
  | 'ugcDataUseFocusedTaskReviewScreenDwelledEvent'
  | 'ugcDataUseSelectAllAppsEvent'
  | 'ugcDataUseExpandedFullEvent'
  | 'ugcDataUseExpandedPartialEvent'
  | 'ugcDataUseExpandedLimitedEvent'
  | 'ugcDataUseClickedInfoLinkFromExpandEvent'
  | 'ugcDataUseClickedInfoLinkFromSubtitleEvent'
  | 'ugcDataUseV2SelectExceptionsEvent'
  | 'ugcDataUseV2ConfirmExceptionsEvent'
  | 'ugcDataUseV2PageDwelledEvent'
  | 'ugcDataUseV2ViewBenefitsModalEvent'
  | 'ugcDataUseV2SaveContributionSelectionEvent';

export enum UgcDataUsePolicyScreens {
  ugcDataUsePageScreen = 'ugcDataUsePageScreen',
  ugcDataUseFocusedTaskSelectAppsScreen = 'ugcDataUseFocusedTaskSelectAppsScreen',
  ugcDataUseFocusedTaskSelectPreferenceScreen = 'ugcDataUseFocusedTaskSelectPreferenceScreen',
  ugcDataUseFocusedTaskReviewScreen = 'ugcDataUseFocusedTaskReviewScreen',
  ugcDataUpdatingScreen = 'ugcDataUpdatingScreen',
  ugcDataUseV2PageScreen = 'ugcDataUseV2PageScreen',
  ugcDataUseV2SelectSpacesScreen = 'ugcDataUseV2SelectSpacesScreen',
  ugcDataUseV2SelectAppsScreen = 'ugcDataUseV2SelectAppsScreen',
  ugcDataUseV2SelectConnectorsScreen = 'ugcDataUseV2SelectConnectorsScreen',
  ugcDataUseV2BenefitScreen = 'ugcDataUseV2BenefitScreen',
}

export const ugcDataUsePolicyEvents: FeatureEvents<ScreenKeys, UIEventKeys, string, string> = {
  confluenceSpec: 'https://product-fabric.atlassian.net/wiki/spaces/SA/pages/5553848338/UGC+Data+Use+-+Analytics+Spec',
  screen: {
    ugcDataUsePageScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUsePageScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85000',
    }),
    ugcDataUseFocusedTaskSelectAppsScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectAppsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82438',
    }),
    ugcDataUseFocusedTaskSelectPreferenceScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82439',
    }),
    ugcDataUseFocusedTaskReviewScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskReviewScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82440',
    }),
    ugcDataUseV2PageScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUseV2PageScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94943',
    }),
    ugcDataUseV2SelectSpacesScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUseV2SelectSpacesScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94944',
    }),
    ugcDataUseV2SelectAppsScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUseV2SelectAppsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94945',
    }),
    ugcDataUseV2SelectConnectorsScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUseV2SelectConnectorsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94946',
    }),
    ugcDataUseV2BenefitScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUseV2BenefitScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94947',
    }),
  },
  ui: {
    ugcDataUseUpdateSettingsClickEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseUpdateSettings',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUpdatingScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82244',
    }),
    ugcDataUseConfirmSettingsClickEvent: (attributes: { orgId: string; userId: string; workspaceIds: Set<string> }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseConfirmSettings',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUpdatingScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82243',
    }),
    ugcDataUsePageDwelledEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Dwelled,
        actionSubjectId: 'ugcDataUsePageDwelled',
        actionSubject: ActionSubject.Page,
        source: UgcDataUsePolicyScreens.ugcDataUsePageScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82638',
    }),
    ugcDataUseFocusedTaskSelectAppsScreenDwelledEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Dwelled,
        actionSubjectId: 'ugcDataUseFocusedTaskSelectAppsScreenDwelled',
        actionSubject: ActionSubject.Page,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectAppsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82639',
    }),
    ugcDataUseFocusedTaskSelectPreferenceScreenDwelledEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Dwelled,
        actionSubjectId: 'ugcDataUseFocusedTaskSelectPreferenceScreenDwelled',
        actionSubject: ActionSubject.Page,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82640',
    }),
    ugcDataUseFocusedTaskReviewScreenDwelledEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Dwelled,
        actionSubjectId: 'ugcDataUseFocusedTaskReviewScreenDwelled',
        actionSubject: ActionSubject.Page,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskReviewScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82641',
    }),
    ugcDataUseSelectAllAppsEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseSelectAllApps',
        actionSubject: ActionSubject.Checkbox,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectAppsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85166',
    }),
    ugcDataUseExpandedFullEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseExpandedFull',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85172',
    }),
    ugcDataUseExpandedPartialEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseExpandedPartial',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85173',
    }),
    ugcDataUseExpandedLimitedEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseExpandedLimited',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85174',
    }),
    ugcDataUseClickedInfoLinkFromExpandEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseClickedInfoLinkFromExpand',
        actionSubject: ActionSubject.Link,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85175',
    }),
    ugcDataUseClickedInfoLinkFromSubtitleEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseClickedInfoLinkFromSubtitle',
        actionSubject: ActionSubject.Link,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85176',
    }),
    ugcDataUseV2SelectExceptionsEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseV2SelectExceptions',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUseV2PageScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94948',
    }),
    ugcDataUseV2ConfirmExceptionsEvent: (attributes: {
      orgId: string;
      userId: string;
      workspaceIds: Set<string>;
      spaceIds: Set<string>;
      connectorIds: Set<string>;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseV2ConfirmExceptions',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUseV2SelectAppsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94949',
    }),
    ugcDataUseV2PageDwelledEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Dwelled,
        actionSubjectId: 'ugcDataUseV2PageDwelled',
        actionSubject: ActionSubject.Page,
        source: UgcDataUsePolicyScreens.ugcDataUseV2PageScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94950',
    }),
    ugcDataUseV2ViewBenefitsModalEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseV2ViewBenefitsModal',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUseV2PageScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94961',
    }),
    ugcDataUseV2SaveContributionSelectionEvent: (attributes: {
      orgId: string;
      userId: string;
      dataSharingSelection: string;
      metadataSharingSelection: string;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseV2SaveContributionSelection',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUseV2PageScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/94965',
    }),
  },
  track: {},
  operational: {},
};
